import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plug, CheckCircle2, XCircle, RefreshCw, AlertCircle } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Skeleton } from '../../components/ui/Skeleton';
import { api, ApiError } from '../../lib/api';
import { PlanonReferencePanel } from './planon/PlanonReferencePanel';

interface Status {
  configured: boolean;
  /** Stations currently wired for a Planon push (e.g. ['Fleet']). */
  stations?: string[];
}

interface BackfillResult {
  scanned: number;
  enqueued: number;
}

const ALLOWED = ['admin', 'coordinator'];

/**
 * Planon integration admin (CLAUDE.md phase 3 item 24).
 */
export function PlanonAdmin() {
  const { data: user, isLoading } = useCurrentUser();
  const [status, setStatus] = useState<Status | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<Status>('/api/admin/planon/status');
        if (!cancelled) setStatus(r);
      } catch {
        if (!cancelled) setStatus({ configured: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please{' '}
        <Link to="/login" className="underline">
          sign in
        </Link>
        .
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Planon integration is restricted to admin / coordinator.
      </main>
    );
  }

  async function backfill() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await api<BackfillResult>('/api/admin/planon/backfill', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      setResult(r);
      toast.success(`Enqueued ${r.enqueued} of ${r.scanned}`);
      setConfirmOpen(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'backfill failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Plug className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">Admin</div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Planon integration
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              When a tracked asset is issued (Fleet / IT / Tools), transitions its Planon record to
              Assigned and adds an issued comment via the CAFM REST API.
            </p>
          </div>
        </header>

        <section className="mt-6 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          <div className="border-b border-line/10 px-5 py-3">
            <h2 className="text-sm font-semibold">Status</h2>
          </div>
          <div className="px-5 py-4">
            {status === null ? (
              <Skeleton className="h-6 w-2/3" />
            ) : status.configured ? (
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div>
                  <div className="font-semibold text-fg">Configured</div>
                  <div className="text-fg-muted">
                    Fires on every kit issue for a tracked asset.
                    {status.stations && status.stations.length > 0 ? (
                      <>
                        {' '}
                        Wired stations:{' '}
                        <span className="font-medium text-fg">{status.stations.join(', ')}</span>.
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-fg-dim" />
                <div>
                  <div className="font-semibold text-fg">Not configured</div>
                  <div className="text-fg-muted">
                    Set <code className="rounded bg-surface-2 px-1 text-xs">PLANON_BASE_URL</code>{' '}
                    and <code className="rounded bg-surface-2 px-1 text-xs">PLANON_API_KEY</code> on
                    the API server.
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          <div className="border-b border-line/10 px-5 py-3">
            <h2 className="text-sm font-semibold">Backfill</h2>
          </div>
          <div className="p-5 sm:p-6">
            <p className="text-sm text-fg-muted">
              Run this once after first wiring Planon up to seed the existing kit history. Live
              events flow automatically thereafter.
            </p>
            <div className="mt-4">
              <Button
                variant="primary"
                size="md"
                iconLeft={<RefreshCw className="h-4 w-4" />}
                loading={running}
                disabled={!status?.configured}
                onClick={() => setConfirmOpen(true)}
              >
                Backfill all asset events
              </Button>
            </div>

            <ConfirmModal
              open={confirmOpen}
              tone="warning"
              icon={RefreshCw}
              title="Backfill every asset event to Planon?"
              message={
                <>
                  Replays every past kit issue through the Planon sync queue. Jobs are de-duplicated
                  per asset, so running twice is safe.
                </>
              }
              confirmLabel="Start backfill"
              cancelLabel="Cancel"
              onConfirm={backfill}
              onClose={() => setConfirmOpen(false)}
            />

            {error ? (
              <div className="mt-3 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}
            {result ? (
              <div className="mt-3 rounded-md border border-line/10 bg-surface-2 p-3 text-sm">
                Scanned <strong className="tabular-nums">{result.scanned}</strong> events; enqueued{' '}
                <strong className="tabular-nums">{result.enqueued}</strong> Planon syncs.
              </div>
            ) : null}
          </div>
        </section>

        <PlanonReferencePanel />
      </div>
    </main>
  );
}
